import {
  Page,
  Layout,
  Card,
  IndexTable,
  Badge,
  Banner,
  Button,
  TextField,
  Select,
  BlockStack,
  Text,
  InlineStack,
  Divider,
  Tabs,
  Spinner,
  Tag,
  useIndexResourceState,
} from '@shopify/polaris';
import { DeleteIcon, PlusIcon, ArrowLeftIcon } from '@shopify/polaris-icons';
import { useState, useEffect, useCallback, useRef, useMemo, type DragEvent, type MouseEvent } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  Panel,
  useNodesState,
  useEdgesState,
  addEdge,
  Handle,
  Position,
  type Node,
  type Edge,
  type OnConnect,
  type NodeTypes,
  type ReactFlowInstance,
  MarkerType,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { apiGet, apiPost, apiPut, apiDelete } from '../api';

// ─── Types ─────────────────────────────────────────────────────

const LOCALES = [
  { code: 'es', name: 'Español' },
  { code: 'en', name: 'English' },
  { code: 'pt', name: 'Português' },
  { code: 'fr', name: 'Français' },
  { code: 'it', name: 'Italiano' },
  { code: 'nl', name: 'Nederlands' },
  { code: 'de', name: 'Deutsch' },
  { code: 'pl', name: 'Polski' },
];

const NODE_TYPE_OPTIONS = [
  { value: 'trigger', label: '▶ Trigger', color: '#16a34a', icon: '▶' },
  { value: 'content', label: '📄 Content', color: '#2563eb', icon: '📄' },
  { value: 'survey', label: '📊 Survey', color: '#9333ea', icon: '📊' },
  { value: 'timer', label: '⏱ Timer', color: '#ea580c', icon: '⏱' },
  { value: 'condition', label: '🔀 Condition', color: '#ca8a04', icon: '🔀' },
  { value: 'action', label: '⚡ Action', color: '#dc2626', icon: '⚡' },
  { value: 'review_request', label: '⭐ Review', color: '#f59e0b', icon: '⭐' },
];

const TRIGGER_TYPES = [
  // — Compra —
  { label: '── Compra ──', value: '__header_compra', disabled: true },
  { label: 'Pedido realizado', value: 'order_placed' },
  { label: 'Primera compra', value: 'first_purchase' },
  { label: 'Compra Nº', value: 'nth_purchase' },
  { label: 'Producto específico', value: 'specific_product' },
  { label: 'Carrito abandonado', value: 'cart_abandoned' },
  { label: 'Reembolso procesado', value: 'refund_processed' },
  // — Suscripción —
  { label: '── Suscripción ──', value: '__header_suscripcion', disabled: true },
  { label: 'Recompra activada', value: 'reorder_activated' },
  { label: 'Recompra desactivada', value: 'reorder_deactivated' },
  // — Envío —
  { label: '── Envío ──', value: '__header_envio', disabled: true },
  { label: 'Pedido enviado', value: 'order_shipped' },
  { label: 'En tránsito', value: 'order_in_transit' },
  { label: 'En reparto', value: 'order_out_for_delivery' },
  { label: 'Pedido entregado', value: 'delivered' },
  { label: 'Incidencia de envío', value: 'order_exception' },
  { label: 'Devuelto', value: 'order_returned' },
  // — Engagement —
  { label: '── Engagement ──', value: '__header_engagement', disabled: true },
  { label: 'Registro en la app', value: 'app_signup' },
  { label: 'Hito de streak', value: 'streak_milestone' },
  { label: 'Streak roto', value: 'streak_broken' },
  { label: 'Hito de puntos', value: 'points_milestone' },
  { label: 'Badge desbloqueado', value: 'badge_unlocked' },
  { label: 'Recompensa canjeada', value: 'reward_redeemed' },
  // — Calendario —
  { label: '── Calendario ──', value: '__header_calendario', disabled: true },
  { label: 'Fecha específica', value: 'specific_date' },
  // — Inactividad —
  { label: '── Inactividad ──', value: '__header_inactividad', disabled: true },
  { label: 'Cliente inactivo', value: 'customer_inactive' },
  // — Nivel —
  { label: '── Nivel ──', value: '__header_nivel', disabled: true },
  { label: 'Cambio de nivel', value: 'tier_change' },
];

const TRIGGER_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  TRIGGER_TYPES.filter((t) => !t.value.startsWith('__')).map((t) => [t.value, t.label]),
);

const TIER_DIRECTION_OPTIONS = [
  { label: 'Cualquiera', value: 'any' },
  { label: 'Subida', value: 'up' },
  { label: 'Bajada', value: 'down' },
];

const STREAK_MILESTONE_OPTIONS = [
  { label: '7 días', value: '7' },
  { label: '14 días', value: '14' },
  { label: '30 días', value: '30' },
  { label: '60 días', value: '60' },
  { label: '90 días', value: '90' },
];

const CONDITION_TYPES = [
  { label: 'Survey response', value: 'survey_response' },
  { label: 'Content viewed', value: 'content_viewed' },
  { label: 'Survey completed', value: 'survey_completed' },
  { label: 'No ha comprado en X días', value: 'no_purchase_in_days' },
  { label: 'Ha comprado producto', value: 'has_purchased_product' },
  { label: 'Order value', value: 'order_value' },
  { label: 'Has previous orders', value: 'has_previous_orders' },
  { label: 'Has reorder contract', value: 'has_reorder_contract' },
  { label: 'Customer level', value: 'customer_level' },
  { label: 'Streak active', value: 'streak_active' },
  { label: 'Points balance', value: 'points_balance' },
  { label: 'Customer locale', value: 'customer_locale' },
  { label: 'Platform', value: 'platform' },
  { label: 'Estado de envío', value: 'shipping_status' },
];

const OPERATORS = [
  { label: 'Equals', value: 'equals' },
  { label: 'Not equals', value: 'not_equals' },
  { label: 'Greater than', value: 'greater_than' },
  { label: 'Less than', value: 'less_than' },
  { label: 'Contains', value: 'contains' },
];

const PRODUCT_TRIGGER_TYPES = new Set([
  'order_placed',
  'first_purchase',
  'nth_purchase',
  'specific_product',
  'cart_abandoned',
  'refund_processed',
  'delivered',
  'reorder_activated',
  'reorder_deactivated',
]);

const ACTION_TYPES = [
  { label: 'Otorgar puntos', value: 'award_points' },
  { label: 'Otorgar badge', value: 'award_badge' },
  { label: 'Generar descuento', value: 'generate_discount' },
  { label: 'Enrollar en journey', value: 'enroll_journey' },
  { label: 'Etiquetar cliente', value: 'tag_customer' },
];

const BADGE_OPTIONS = [
  { slug: 'first_sprout', label: '🌱 Primer Brote' },
  { slug: 'streak_5', label: '🔥 Racha de 5' },
  { slug: 'streak_10', label: '💪 Racha de 10' },
  { slug: 'streak_30', label: '🏆 Racha de 30' },
  { slug: 'ritual_complete', label: '🍵 Ritual Completo' },
  { slug: 'matcha_artist', label: '📸 Matcha Artist' },
  { slug: 'first_tree', label: '🌳 Primer Árbol' },
  { slug: 'garden_starter', label: '🏡 Jardín Inicial' },
  { slug: 'garden_mature', label: '🌲 Jardín Maduro' },
  { slug: 'ambassador', label: '👥 Embajador' },
  { slug: 'journalist', label: '📝 Periodista' },
  { slug: 'zen_master', label: '🧘 Zen Master' },
];

const DISCOUNT_TYPES = [
  { label: 'Porcentaje (%)', value: 'percentage' },
  { label: 'Importe fijo (€)', value: 'fixed_amount' },
];

const DELAY_TYPES = [
  { label: 'Relative (hours)', value: 'relative' },
  { label: 'Absolute (date)', value: 'absolute' },
];

interface JourneyListItem {
  id: string;
  name: string;
  description: string | null;
  priority: number;
  active: boolean;
  nodeCount: number;
  productCount: number;
  enrollmentCount: number;
  productIds: string[];
  createdAt: string;
}

interface ContentForm {
  locale: string;
  title: string;
  body: string;
  imageUrl: string;
  videoUrl: string;
  pushTitle: string;
  pushBody: string;
}

interface ProductOption {
  id: string;
  title: string;
  handle?: string;
  image?: string | null;
  imageUrl: string | null;
}

interface SurveyOption {
  id: string;
  title: string;
}

// ─── Custom Node Components ────────────────────────────────────

const nodeStyles = {
  trigger: { background: '#dcfce7', borderColor: '#16a34a', color: '#15803d' },
  content: { background: '#dbeafe', borderColor: '#2563eb', color: '#1d4ed8' },
  survey: { background: '#f3e8ff', borderColor: '#9333ea', color: '#7e22ce' },
  timer: { background: '#ffedd5', borderColor: '#ea580c', color: '#c2410c' },
  condition: { background: '#fef9c3', borderColor: '#ca8a04', color: '#a16207' },
  action: { background: '#fee2e2', borderColor: '#dc2626', color: '#b91c1c' },
};

function TriggerNode({ id, data }: { id: string; data: Record<string, unknown> }) {
  const config = (data.config ?? {}) as Record<string, unknown>;
  const triggerType = (config.triggerType as string) ?? 'order_placed';
  const label = TRIGGER_TYPE_LABELS[triggerType] ?? triggerType;

  // Build config description
  let configDesc = '';
  if (triggerType === 'nth_purchase' && config.orderNumber) {
    configDesc = `#${config.orderNumber}`;
  } else if (triggerType === 'customer_inactive' && config.inactiveDays) {
    configDesc = `${config.inactiveDays}d`;
  } else if (triggerType === 'streak_milestone' && config.days) {
    configDesc = `${config.days}d`;
  } else if (triggerType === 'points_milestone' && config.points) {
    configDesc = `≥${config.points}pts`;
  } else if (triggerType === 'tier_change' && config.direction) {
    configDesc = config.direction === 'up' ? '↑' : config.direction === 'down' ? '↓' : '↕';
  } else if (triggerType === 'specific_date' && config.date) {
    configDesc = `${config.date}${config.recurring ? ' 🔁' : ''}`;
  } else if (triggerType === 'specific_product' && config.shopifyProductId) {
    configDesc = `${(config.shopifyProductId as string).slice(-8)}`;
  }

  return (
    <div style={{
      ...baseNodeStyle,
      ...nodeStyles.trigger,
      borderRadius: 24,
    }}>
      <Handle type="target" position={Position.Top} style={handleStyle} />
      <div style={nodeHeaderStyle}>▶ Trigger</div>
      <div style={nodeSubStyle}>{label}{configDesc ? ` (${configDesc})` : ''}</div>
      <AnalyticsBadge nodeId={id} />
      <Handle type="source" position={Position.Bottom} id="default" style={handleStyle} />
    </div>
  );
}

// Global analytics store — read by node components directly
let _globalAnalytics: Record<string, { reached: number; completed: number; waiting: number }> = {};

function AnalyticsBadge({ nodeId }: { nodeId: string }) {
  const stats = _globalAnalytics[nodeId];
  if (!stats) return null;
  if (!stats.reached && !stats.completed && !stats.waiting) return null;
  return (
    <div style={{
      display: 'flex', gap: 3, justifyContent: 'center',
      fontSize: 9, fontWeight: 700, marginTop: 4,
    }}>
      <span title="Llegaron" style={{
        background: '#3b82f6', color: '#fff', borderRadius: 8,
        padding: '1px 5px', lineHeight: '16px',
      }}>👁 {stats.reached}</span>
      <span title="Completaron" style={{
        background: '#16a34a', color: '#fff', borderRadius: 8,
        padding: '1px 5px', lineHeight: '16px',
      }}>✓ {stats.completed}</span>
      {stats.waiting > 0 && (
        <span title="Esperando" style={{
          background: '#f59e0b', color: '#fff', borderRadius: 8,
          padding: '1px 5px', lineHeight: '16px',
        }}>⏳ {stats.waiting}</span>
      )}
    </div>
  );
}

function ContentNode({ id, data }: { id: string; data: Record<string, unknown> }) {
  return (
    <div style={{ ...baseNodeStyle, ...nodeStyles.content }}>
      <Handle type="target" position={Position.Top} style={handleStyle} />
      <div style={nodeHeaderStyle}>📄 Content</div>
      <div style={nodeSubStyle}>{(data.label as string) || 'Untitled'}</div>
      <AnalyticsBadge nodeId={id} />
      <Handle type="source" position={Position.Bottom} id="default" style={handleStyle} />
    </div>
  );
}

function SurveyNode({ id, data }: { id: string; data: Record<string, unknown> }) {
  return (
    <div style={{ ...baseNodeStyle, ...nodeStyles.survey }}>
      <Handle type="target" position={Position.Top} style={handleStyle} />
      <div style={nodeHeaderStyle}>📊 Survey</div>
      <div style={nodeSubStyle}>{(data.label as string) || 'Select survey'}</div>
      <AnalyticsBadge nodeId={id} />
      <Handle type="source" position={Position.Bottom} id="default" style={handleStyle} />
    </div>
  );
}

function TimerNode({ id, data }: { id: string; data: Record<string, unknown> }) {
  const config = (data.config ?? {}) as Record<string, unknown>;
  const delayText = config.delayType === 'absolute'
    ? `Until ${config.delayDate ?? '...'}`
    : `Wait ${config.delayHours ?? 24}h`;
  return (
    <div style={{ ...baseNodeStyle, ...nodeStyles.timer }}>
      <Handle type="target" position={Position.Top} style={handleStyle} />
      <div style={nodeHeaderStyle}>⏱ Timer</div>
      <div style={nodeSubStyle}>{delayText}</div>
      <AnalyticsBadge nodeId={id} />
      <Handle type="source" position={Position.Bottom} id="default" style={handleStyle} />
    </div>
  );
}

function ConditionNode({ id, data }: { id: string; data: Record<string, unknown> }) {
  const config = (data.config ?? {}) as Record<string, unknown>;
  let typeLabel = CONDITION_TYPES.find((c) => c.value === config.conditionType)?.label ?? 'Condition';
  if (config.conditionType === 'no_purchase_in_days' && config.days) {
    typeLabel = `Sin compra en ${config.days} días`;
  } else if (config.conditionType === 'has_purchased_product' && config.productTitle) {
    const title = String(config.productTitle);
    typeLabel = `Compró: ${title.length > 20 ? title.slice(0, 20) + '…' : title}`;
  } else if (config.conditionType === 'shipping_status' && config.value) {
    const statusLabels: Record<string, string> = { confirmed: 'Confirmado', preparing: 'Preparando', shipped: 'Enviado', in_transit: 'En tránsito', out_for_delivery: 'En reparto', delivered: 'Entregado', exception: 'Incidencia', returned: 'Devuelto' };
    typeLabel = `Envío: ${statusLabels[config.value as string] ?? config.value}`;
  }
  return (
    <div style={{
      ...baseNodeStyle,
      ...nodeStyles.condition,
      transform: 'rotate(0deg)',
      clipPath: 'none',
    }}>
      <Handle type="target" position={Position.Top} style={handleStyle} />
      <div style={nodeHeaderStyle}>🔀 Condition</div>
      <div style={nodeSubStyle}>{typeLabel}</div>
      <AnalyticsBadge nodeId={id} />
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 11, fontWeight: 600 }}>
        <div style={{ position: 'relative' }}>
          <span style={{ color: '#16a34a' }}>Sí ✓</span>
          <Handle type="source" position={Position.Bottom} id="yes" style={{ ...handleStyle, left: 0, bottom: -14, background: '#16a34a' }} />
        </div>
        <div style={{ position: 'relative' }}>
          <span style={{ color: '#dc2626' }}>No ✗</span>
          <Handle type="source" position={Position.Bottom} id="no" style={{ ...handleStyle, right: 0, left: 'auto', bottom: -14, background: '#dc2626' }} />
        </div>
      </div>
    </div>
  );
}

function ActionNode({ id, data }: { id: string; data: Record<string, unknown> }) {
  const config = (data.config ?? {}) as Record<string, unknown>;
  let desc = 'Action';
  if (config.actionType === 'award_points') desc = `+${config.points ?? 0} pts`;
  else if (config.actionType === 'award_badge') {
    const badge = BADGE_OPTIONS.find((b) => b.slug === config.badgeSlug);
    desc = badge ? badge.label : 'Select badge';
  } else if (config.actionType === 'generate_discount') {
    const val = config.discountValue ?? 0;
    desc = config.discountType === 'fixed_amount' ? `${val}€ off` : `${val}% off`;
  } else if (config.actionType === 'enroll_journey') {
    desc = config.journeyId ? `→ Journey` : 'Select journey';
  } else if (config.actionType === 'tag_customer') {
    desc = config.tag ? `🏷️ ${config.tag}` : 'Set tag';
  }
  return (
    <div style={{ ...baseNodeStyle, ...nodeStyles.action }}>
      <Handle type="target" position={Position.Top} style={handleStyle} />
      <div style={nodeHeaderStyle}>⚡ Action</div>
      <div style={nodeSubStyle}>{desc}</div>
      <AnalyticsBadge nodeId={id} />
      <Handle type="source" position={Position.Bottom} id="default" style={handleStyle} />
    </div>
  );
}

const baseNodeStyle: React.CSSProperties = {
  padding: '12px 16px',
  borderRadius: 12,
  border: '2px solid',
  minWidth: 160,
  maxWidth: 200,
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  fontSize: 13,
};

const nodeHeaderStyle: React.CSSProperties = {
  fontWeight: 700,
  fontSize: 13,
  marginBottom: 4,
};

const nodeSubStyle: React.CSSProperties = {
  fontSize: 12,
  opacity: 0.8,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const handleStyle: React.CSSProperties = {
  width: 10,
  height: 10,
  borderRadius: '50%',
  background: '#6b7280',
  border: '2px solid #fff',
};

// ─── Main Component ────────────────────────────────────────────

export default function Journeys() {
  const [view, setView] = useState<'list' | 'editor'>('list');
  const [editingJourneyId, setEditingJourneyId] = useState<string | null>(null);

  if (view === 'editor') {
    return (
      <JourneyEditor
        journeyId={editingJourneyId}
        onBack={() => { setView('list'); setEditingJourneyId(null); }}
      />
    );
  }

  return (
    <JourneyList
      onEdit={(id) => { setEditingJourneyId(id); setView('editor'); }}
      onCreate={() => { setEditingJourneyId(null); setView('editor'); }}
    />
  );
}

// ─── List View ─────────────────────────────────────────────────

function JourneyList({
  onEdit,
  onCreate,
}: {
  onEdit: (id: string) => void;
  onCreate: () => void;
}) {
  const [journeys, setJourneys] = useState<JourneyListItem[]>([]);
  const [loading, setLoading] = useState(true);

  // Enrollments modal
  const [showEnrollments, setShowEnrollments] = useState(false);
  const [enrollments, setEnrollments] = useState<any[]>([]);
  const [enrollmentsLoading, setEnrollmentsLoading] = useState(false);

  // Backpopulate
  const [backpopulateJourneyId, setBackpopulateJourneyId] = useState<string | null>(null);
  const [backpopulateDate, setBackpopulateDate] = useState('');
  const [backpopulateLoading, setBackpopulateLoading] = useState(false);
  const [backpopulateResult, setBackpopulateResult] = useState<{ enrolled: number; candidatesFound: number } | null>(null);

  const fetchJourneys = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiGet<{ journeys: JourneyListItem[] }>('/panel-api/journeys');
      setJourneys(data.journeys);
    } catch (err) {
      console.error('Failed to fetch journeys:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchJourneys(); }, [fetchJourneys]);

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this journey? This cannot be undone.')) return;
    try {
      await apiDelete(`/panel-api/journeys/${id}`);
      await fetchJourneys();
    } catch (err) {
      console.error('Failed to delete journey:', err);
    }
  };

  const handleViewEnrollments = async (journeyId: string) => {
    setEnrollmentsLoading(true);
    setShowEnrollments(true);
    try {
      const data = await apiGet<{ enrollments: any[] }>(`/panel-api/journeys/${journeyId}/enrollments`);
      setEnrollments(data.enrollments);
    } catch (err) {
      console.error('Failed to fetch enrollments:', err);
    } finally {
      setEnrollmentsLoading(false);
    }
  };

  const handleBackpopulate = async () => {
    if (!backpopulateJourneyId || !backpopulateDate) return;
    setBackpopulateLoading(true);
    setBackpopulateResult(null);
    try {
      const data = await apiPost<{ enrolled: number; candidatesFound: number }>(
        `/panel-api/journeys/${backpopulateJourneyId}/backpopulate`,
        { sinceDate: backpopulateDate },
      );
      setBackpopulateResult(data);
      await fetchJourneys(); // Refresh enrollment counts
    } catch (err) {
      console.error('Failed to backpopulate:', err);
      setBackpopulateResult({ enrolled: 0, candidatesFound: 0 });
    } finally {
      setBackpopulateLoading(false);
    }
  };

  const { selectedResources, allResourcesSelected, handleSelectionChange } =
    useIndexResourceState(journeys as any);

  const rowMarkup = journeys.map((j, idx) => (
    <IndexTable.Row id={j.id} key={j.id} position={idx} selected={selectedResources.includes(j.id)}>
      <IndexTable.Cell>
        <Text as="span" variant="bodyMd" fontWeight="bold">{j.name}</Text>
      </IndexTable.Cell>
      <IndexTable.Cell>{j.priority}</IndexTable.Cell>
      <IndexTable.Cell>
        <Badge tone={j.active ? 'success' : undefined}>
          {j.active ? 'Active' : 'Inactive'}
        </Badge>
      </IndexTable.Cell>
      <IndexTable.Cell>{j.productCount}</IndexTable.Cell>
      <IndexTable.Cell>{j.nodeCount}</IndexTable.Cell>
      <IndexTable.Cell>{j.enrollmentCount}</IndexTable.Cell>
      <IndexTable.Cell>
        <InlineStack gap="200">
          <Button size="slim" onClick={() => onEdit(j.id)}>Edit</Button>
          <Button size="slim" onClick={() => handleViewEnrollments(j.id)}>Enrollments</Button>
          <Button size="slim" onClick={() => { setBackpopulateJourneyId(j.id); setBackpopulateDate(''); setBackpopulateResult(null); }}>Backpopulate</Button>
          <Button size="slim" tone="critical" onClick={() => handleDelete(j.id)}>Delete</Button>
        </InlineStack>
      </IndexTable.Cell>
    </IndexTable.Row>
  ));

  return (
    <Page
      title="Journeys"
      subtitle="Post-purchase content flows (visual editor)"
      primaryAction={{ content: 'Create journey', onAction: onCreate }}
    >
      <Layout>
        <Layout.Section>
          <Card padding="0">
            <IndexTable
              resourceName={{ singular: 'journey', plural: 'journeys' }}
              itemCount={journeys.length}
              selectedItemsCount={allResourcesSelected ? 'All' : selectedResources.length}
              onSelectionChange={handleSelectionChange}
              headings={[
                { title: 'Name' },
                { title: 'Priority' },
                { title: 'Status' },
                { title: 'Products' },
                { title: 'Nodes' },
                { title: 'Enrollments' },
                { title: 'Actions' },
              ]}
              loading={loading}
            >
              {rowMarkup}
            </IndexTable>
          </Card>
        </Layout.Section>
      </Layout>

      {/* Enrollments inline */}
      {showEnrollments && (
        <div style={{ marginTop: 20 }}>
          <Card>
            <BlockStack gap="300">
              <InlineStack align="space-between">
                <Text as="h3" variant="headingMd">Enrolled Customers</Text>
                <Button size="slim" onClick={() => setShowEnrollments(false)}>Close</Button>
              </InlineStack>
              {enrollmentsLoading ? (
                <div style={{ textAlign: 'center', padding: 24 }}><Spinner size="large" /></div>
              ) : enrollments.length === 0 ? (
                <Text as="p" tone="subdued">No enrollments yet.</Text>
              ) : (
                <IndexTable
                  resourceName={{ singular: 'enrollment', plural: 'enrollments' }}
                  itemCount={enrollments.length}
                  selectable={false}
                  headings={[
                    { title: 'Customer' },
                    { title: 'Status' },
                    { title: 'Enrolled' },
                    { title: 'Completed' },
                    { title: '' },
                  ]}
                >
                  {enrollments.map((e: any, idx: number) => (
                    <IndexTable.Row id={e.id} key={e.id} position={idx}>
                      <IndexTable.Cell>
                        <Text as="span" variant="bodyMd" fontWeight="bold">
                          {e.customerEmail ?? 'Unknown'}
                        </Text>
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        <Badge tone={e.status === 'active' ? 'success' : e.status === 'completed' ? 'info' : undefined}>
                          {e.status}
                        </Badge>
                      </IndexTable.Cell>
                      <IndexTable.Cell>{new Date(e.enrolledAt).toLocaleDateString()}</IndexTable.Cell>
                      <IndexTable.Cell>{e.completedAt ? new Date(e.completedAt).toLocaleDateString() : '—'}</IndexTable.Cell>
                      <IndexTable.Cell>
                        <Button
                          size="slim"
                          variant="plain"
                          tone="critical"
                          onClick={async () => {
                            if (!confirm(`¿Eliminar enrollment de ${e.customerEmail}?`)) return;
                            try {
                              await apiDelete(`/panel-api/journeys/enrollments/${e.id}`);
                              setEnrollments((prev) => prev.filter((en: any) => en.id !== e.id));
                            } catch (err) {
                              alert('Error eliminando enrollment');
                            }
                          }}
                        >
                          Eliminar
                        </Button>
                      </IndexTable.Cell>
                    </IndexTable.Row>
                  ))}
                </IndexTable>
              )}
            </BlockStack>
          </Card>
        </div>
      )}

      {/* Backpopulate inline */}
      {backpopulateJourneyId && (
        <div style={{ marginTop: 20 }}>
          <Card>
            <BlockStack gap="300">
              <InlineStack align="space-between">
                <Text as="h3" variant="headingMd">
                  Back populate: {journeys.find((j) => j.id === backpopulateJourneyId)?.name}
                </Text>
                <Button size="slim" onClick={() => setBackpopulateJourneyId(null)}>Close</Button>
              </InlineStack>
              <Text as="p" tone="subdued" variant="bodySm">
                Enrolla retroactivamente a los clientes que cumplieron el trigger desde la fecha indicada. Se inician desde el primer paso del journey.
              </Text>
              <InlineStack gap="300" blockAlign="end">
                <div style={{ flex: 1 }}>
                  <TextField
                    label="Desde fecha"
                    type="date"
                    value={backpopulateDate}
                    onChange={setBackpopulateDate}
                    autoComplete="off"
                  />
                </div>
                <Button
                  variant="primary"
                  onClick={handleBackpopulate}
                  disabled={backpopulateLoading || !backpopulateDate}
                >
                  {backpopulateLoading ? 'Procesando...' : 'Ejecutar'}
                </Button>
              </InlineStack>
              {backpopulateResult && (
                <Banner
                  title={backpopulateResult.enrolled > 0 ? 'Back populate completado' : 'Sin nuevos enrollments'}
                  tone={backpopulateResult.enrolled > 0 ? 'success' : 'info'}
                >
                  <p>
                    Candidatos encontrados: {backpopulateResult.candidatesFound} · Enrollados: {backpopulateResult.enrolled}
                  </p>
                </Banner>
              )}
            </BlockStack>
          </Card>
        </div>
      )}
    </Page>
  );
}

// ─── Editor View ───────────────────────────────────────────────

function JourneyEditor({
  journeyId,
  onBack,
}: {
  journeyId: string | null;
  onBack: () => void;
}) {
  // Journey metadata
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('0');
  const [active, setActive] = useState(true);
  const [selectedProducts, setSelectedProducts] = useState<ProductOption[]>([]);

  // Product search
  const [productSearch, setProductSearch] = useState('');
  const [productResults, setProductResults] = useState<ProductOption[]>([]);
  const [searchingProducts, setSearchingProducts] = useState(false);

  // Survey options
  const [surveyOptions, setSurveyOptions] = useState<SurveyOption[]>([]);

  // Journey options (for "enroll in journey" action)
  const [journeyOptions, setJourneyOptions] = useState<{ id: string; name: string }[]>([]);

  // React Flow state
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [rfInstance, setRfInstance] = useState<ReactFlowInstance | null>(null);
  const reactFlowWrapper = useRef<HTMLDivElement>(null);

  // Selected node for properties panel
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);

  // Node content state (keyed by nodeId)
  const [nodeContentMap, setNodeContentMap] = useState<Map<string, ContentForm[]>>(new Map());

  // Locale tab for content editing
  const [localeTab, setLocaleTab] = useState(0);

  // Content Library items for the selector
  const [contentLibraryItems, setContentLibraryItems] = useState<Array<{
    id: string; internalName: string; contentType: string; active?: boolean;
    locales: Array<{ locale: string; title: string; body: string; mediaUrls: string[]; pushTitle: string; pushBody: string }>;
  }>>([]);

  // Saving
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!journeyId);

  // Validation errors
  const [validationError, setValidationError] = useState<string | null>(null);

  // Analytics: per-node reached/completed counts
  const [analytics, setAnalytics] = useState<{
    totalEnrollments: number;
    completedEnrollments: number;
    nodes: Record<string, { reached: number; completed: number; waiting: number }>;
  } | null>(null);
  const [reenrolling, setReenrolling] = useState(false);

  // Counter for generating temp node IDs
  const nodeIdCounter = useRef(1);

  // Register custom node types
  const nodeTypes: NodeTypes = useMemo(() => ({
    trigger: TriggerNode,
    content: ContentNode,
    survey: SurveyNode,
    timer: TimerNode,
    condition: ConditionNode,
    action: ActionNode,
  }), []);

  // ─── Load Data ───────────────────────────────────────

  // Load content library
  useEffect(() => {
    apiGet<{ content: typeof contentLibraryItems }>('/panel-api/content')
      .then((data) => setContentLibraryItems(data.content.filter((c) => c.active !== false)))
      .catch((err) => console.error('[ContentLibrary] load failed:', err));
  }, []);

  useEffect(() => {
    fetchSurveys();
    fetchJourneyOptions();
    if (journeyId) {
      loadJourney(journeyId).then(() => {
        // Fetch analytics AFTER journey is loaded (nodes must exist first)
        apiGet<any>(`/panel-api/journeys/${journeyId}/analytics`)
          .then((data) => {
            console.log('[Analytics] loaded:', data);
            setAnalytics(data);
          })
          .catch((err) => console.error('[Analytics] failed:', err));
      });
    } else {
      // New journey — add a default trigger node
      const triggerId = `node_${nodeIdCounter.current++}`;
      setNodes([{
        id: triggerId,
        type: 'trigger',
        position: { x: 300, y: 50 },
        data: { config: { triggerType: 'order_placed' }, label: 'Order placed' },
      }]);
    }
  }, [journeyId]);

  const fetchSurveys = async () => {
    try {
      const data = await apiGet<{ surveys: SurveyOption[] }>('/panel-api/surveys');
      setSurveyOptions(data.surveys);
    } catch (err) {
      console.error('Failed to fetch surveys:', err);
    }
  };

  const fetchJourneyOptions = async () => {
    try {
      const data = await apiGet<{ journeys: { id: string; name: string }[] }>('/panel-api/journeys');
      setJourneyOptions(data.journeys.filter((j) => j.id !== journeyId));
    } catch (err) {
      console.error('Failed to fetch journeys:', err);
    }
  };

  const loadJourney = async (id: string) => {
    setLoading(true);
    try {
      const data = await apiGet<{ journey: any }>(`/panel-api/journeys/${id}`);
      const j = data.journey;
      setName(j.name);
      setDescription(j.description ?? '');
      setPriority(String(j.priority));
      setActive(j.active);

      // Load products
      const productGids = j.products as string[];
      setSelectedProducts(productGids.map((gid: string) => ({
        id: gid,
        title: gid.replace('gid://shopify/Product/', 'Product #'),
        imageUrl: null,
      })));

      // Load nodes
      const contentMap = new Map<string, ContentForm[]>();
      const loadedNodes: Node[] = (j.nodes ?? []).map((n: any) => {
        const nodeId = n.id;
        nodeIdCounter.current++;

        // Load content for this node
        if (n.content?.length) {
          const contentForms: ContentForm[] = LOCALES.map((l) => {
            const existing = n.content.find((c: any) => c.locale === l.code);
            return {
              locale: l.code,
              title: existing?.title ?? '',
              body: existing?.body ?? '',
              imageUrl: existing?.imageUrl ?? '',
              videoUrl: existing?.videoUrl ?? '',
              pushTitle: existing?.pushTitle ?? '',
              pushBody: existing?.pushBody ?? '',
            };
          });
          contentMap.set(nodeId, contentForms);
        }

        // Determine label
        let label = '';
        const config = n.config ?? {};
        if (n.nodeType === 'content') {
          const esContent = n.content?.find((c: any) => c.locale === 'es');
          label = esContent?.title ?? '';
        } else if (n.nodeType === 'survey') {
          label = config.surveyId ? `Survey: ${config.surveyId.slice(0, 8)}...` : 'Select survey';
        } else if (n.nodeType === 'review_request') {
          label = config.useOrderProducts ? 'Review: Productos del pedido' : (config.specificProductTitle as string || 'Review: Seleccionar producto');
        }

        return {
          id: nodeId,
          type: n.nodeType,
          position: { x: n.positionX ?? 0, y: n.positionY ?? 0 },
          data: { config: n.config ?? {}, label },
        };
      });

      // Load edges
      const loadedEdges: Edge[] = (j.edges ?? []).map((e: any) => ({
        id: e.id,
        source: e.sourceNodeId,
        target: e.targetNodeId,
        sourceHandle: e.sourceHandle ?? 'default',
        markerEnd: { type: MarkerType.ArrowClosed },
        style: { strokeWidth: 2 },
      }));

      setNodeContentMap(contentMap);
      setNodes(loadedNodes);
      setEdges(loadedEdges);
    } catch (err) {
      console.error('Failed to load journey:', err);
    } finally {
      setLoading(false);
    }
  };

  // ─── Product Search ──────────────────────────────────

  const searchProducts = useCallback(async (q: string) => {
    if (!q || q.length < 2) { setProductResults([]); return; }
    setSearchingProducts(true);
    try {
      const data = await apiGet<{ products: ProductOption[] }>(`/panel-api/products/search?q=${encodeURIComponent(q)}`);
      setProductResults(data.products);
    } catch (err) {
      console.error('Failed to search products:', err);
    } finally {
      setSearchingProducts(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => { searchProducts(productSearch); }, 300);
    return () => clearTimeout(timer);
  }, [productSearch, searchProducts]);

  // Inject analytics into node data so React Flow re-renders them
  useEffect(() => {
    if (!analytics?.nodes) return;
    console.log('[Analytics] injecting, node keys:', Object.keys(analytics.nodes));
    _globalAnalytics = analytics.nodes;
    // Must update node data to trigger React Flow re-render (it memoizes nodes)
    setNodes((nds) => {
      console.log('[Analytics] updating', nds.length, 'nodes');
      return nds.map((n) => ({
        ...n,
        data: { ...n.data, _av: Date.now() }, // _av = analytics version, forces new reference
      }));
    });
  }, [analytics]);

  // ─── Node Operations ─────────────────────────────────

  const onDragOver = useCallback((event: DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback((event: DragEvent) => {
    event.preventDefault();
    const nodeType = event.dataTransfer.getData('application/reactflow');
    if (!nodeType || !rfInstance || !reactFlowWrapper.current) return;

    // Prevent adding a second trigger
    if (nodeType === 'trigger' && nodes.some((n) => n.type === 'trigger')) return;

    const bounds = reactFlowWrapper.current.getBoundingClientRect();
    const position = rfInstance.screenToFlowPosition({
      x: event.clientX - bounds.left,
      y: event.clientY - bounds.top,
    });

    const newId = `node_${nodeIdCounter.current++}`;
    const defaultConfig = getDefaultConfig(nodeType);

    const newNode: Node = {
      id: newId,
      type: nodeType,
      position,
      data: { config: defaultConfig, label: '' },
    };

    setNodes((nds) => [...nds, newNode]);

    // Initialize content for content nodes
    if (nodeType === 'content') {
      setNodeContentMap((prev) => {
        const next = new Map(prev);
        next.set(newId, LOCALES.map((l) => ({
          locale: l.code, title: '', body: '', imageUrl: '', videoUrl: '', pushTitle: '', pushBody: '',
        })));
        return next;
      });
    }
  }, [rfInstance, setNodes]);

  // ─── Drop-to-create: track pending connection ────────

  const pendingConnection = useRef<{ nodeId: string; handleId: string | null } | null>(null);
  const connectionSucceeded = useRef(false);
  const dropMenuOpenedAt = useRef(0);
  const [dropMenu, setDropMenu] = useState<{ x: number; y: number; flowX: number; flowY: number } | null>(null);

  const onConnectStart = useCallback((_: any, params: { nodeId: string | null; handleId: string | null }) => {
    connectionSucceeded.current = false;
    if (params.nodeId) {
      pendingConnection.current = { nodeId: params.nodeId, handleId: params.handleId };
    }
  }, []);

  // Wrap onConnect to track successful connections
  const onConnectWrapped: OnConnect = useCallback((params) => {
    connectionSucceeded.current = true;
    setEdges((eds) =>
      addEdge({
        ...params,
        markerEnd: { type: MarkerType.ArrowClosed },
        style: { strokeWidth: 2 },
      } as Edge, eds),
    );
  }, [setEdges]);

  const onConnectEnd = useCallback((event: any) => {
    // If connection succeeded (dropped on a valid handle), don't show menu
    if (connectionSucceeded.current) {
      pendingConnection.current = null;
      connectionSucceeded.current = false;
      return;
    }

    if (!pendingConnection.current || !rfInstance || !reactFlowWrapper.current) {
      pendingConnection.current = null;
      return;
    }

    // Dropped on empty canvas — show node creation menu
    const bounds = reactFlowWrapper.current.getBoundingClientRect();
    const clientX = 'clientX' in event ? event.clientX : event.changedTouches?.[0]?.clientX ?? 0;
    const clientY = 'clientY' in event ? event.clientY : event.changedTouches?.[0]?.clientY ?? 0;

    // screenToFlowPosition takes absolute screen coords in xyflow v12
    const flowPosition = rfInstance.screenToFlowPosition({ x: clientX, y: clientY });

    dropMenuOpenedAt.current = Date.now();
    setDropMenu({
      x: clientX - bounds.left,
      y: clientY - bounds.top,
      flowX: flowPosition.x,
      flowY: flowPosition.y,
    });
  }, [rfInstance]);

  const createNodeFromDrop = useCallback((nodeType: string) => {
    if (!dropMenu || !pendingConnection.current) return;

    // Capture ref immediately before any state changes or side effects
    const source = { ...pendingConnection.current };

    const newId = `node_${nodeIdCounter.current++}`;
    const defaultConfig = getDefaultConfig(nodeType);

    const newNode: Node = {
      id: newId,
      type: nodeType,
      position: { x: dropMenu.flowX - 80, y: dropMenu.flowY },
      data: { config: defaultConfig, label: '' },
    };

    setNodes((nds) => [...nds, newNode]);

    // Initialize content for content nodes
    if (nodeType === 'content') {
      setNodeContentMap((prev) => {
        const next = new Map(prev);
        next.set(newId, LOCALES.map((l) => ({
          locale: l.code, title: '', body: '', imageUrl: '', videoUrl: '', pushTitle: '', pushBody: '',
        })));
        return next;
      });
    }

    // Create edge from source to new node
    setEdges((eds) =>
      addEdge({
        id: `edge_${source.nodeId}_${newId}`,
        source: source.nodeId,
        sourceHandle: source.handleId ?? 'default',
        target: newId,
        markerEnd: { type: MarkerType.ArrowClosed },
        style: { strokeWidth: 2 },
      } as Edge, eds),
    );

    // Select new node
    setSelectedNodeId(newId);
    setSelectedEdgeId(null);

    // Cleanup
    setDropMenu(null);
    pendingConnection.current = null;
  }, [dropMenu, setNodes, setEdges]);

  const closeDropMenu = useCallback(() => {
    setDropMenu(null);
    pendingConnection.current = null;
  }, []);

  // Selected edge for deletion
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);

  // Apply selected styling to edges
  const styledEdges = useMemo(() =>
    edges.map((e) => ({
      ...e,
      style: {
        ...e.style,
        strokeWidth: e.id === selectedEdgeId ? 3 : 2,
        stroke: e.id === selectedEdgeId ? '#2563eb' : undefined,
      },
      animated: e.id === selectedEdgeId,
    })),
  [edges, selectedEdgeId]);

  const onNodeClick = useCallback((_: MouseEvent, node: Node) => {
    setSelectedNodeId(node.id);
    setSelectedEdgeId(null);
    setLocaleTab(0);
  }, []);

  const onEdgeClick = useCallback((_: MouseEvent, edge: Edge) => {
    setSelectedNodeId(null);
    setSelectedEdgeId(edge.id);
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedNodeId(null);
    setSelectedEdgeId(null);
    // Don't close drop menu if it just opened (onConnectEnd + onPaneClick fire together)
    if (Date.now() - dropMenuOpenedAt.current > 300) {
      closeDropMenu();
    }
  }, [closeDropMenu]);

  const deleteNode = useCallback((nodeId: string) => {
    setNodes((nds) => nds.filter((n) => n.id !== nodeId));
    setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));
    setNodeContentMap((prev) => {
      const next = new Map(prev);
      next.delete(nodeId);
      return next;
    });
    if (selectedNodeId === nodeId) setSelectedNodeId(null);
  }, [selectedNodeId, setNodes, setEdges]);

  const deleteEdge = useCallback((edgeId: string) => {
    setEdges((eds) => eds.filter((e) => e.id !== edgeId));
    if (selectedEdgeId === edgeId) setSelectedEdgeId(null);
  }, [selectedEdgeId, setEdges]);

  // Keyboard handler for deleting selected edges
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (selectedEdgeId && (e.key === 'Delete' || e.key === 'Backspace')) {
        // Don't delete if user is typing in an input
        const target = e.target as HTMLElement;
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;
        e.preventDefault();
        deleteEdge(selectedEdgeId);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedEdgeId, deleteEdge]);

  // ─── Update Node Config ──────────────────────────────

  const updateNodeConfig = useCallback((nodeId: string, configUpdate: Record<string, unknown>) => {
    setNodes((nds) =>
      nds.map((n) => {
        if (n.id !== nodeId) return n;
        const currentConfig = (n.data.config ?? {}) as Record<string, unknown>;
        return {
          ...n,
          data: { ...n.data, config: { ...currentConfig, ...configUpdate } },
        };
      }),
    );
  }, [setNodes]);

  const updateNodeLabel = useCallback((nodeId: string, label: string) => {
    setNodes((nds) =>
      nds.map((n) => n.id === nodeId ? { ...n, data: { ...n.data, label } } : n),
    );
  }, [setNodes]);

  const updateNodeContent = useCallback((nodeId: string, localeIdx: number, field: keyof ContentForm, value: string) => {
    setNodeContentMap((prev) => {
      const next = new Map(prev);
      const contents = [...(next.get(nodeId) ?? [])];
      if (contents[localeIdx]) {
        contents[localeIdx] = { ...contents[localeIdx], [field]: value };
      }
      next.set(nodeId, contents);
      return next;
    });

    // Update node label from title (es)
    if (field === 'title' && localeIdx === 0) {
      updateNodeLabel(nodeId, value);
    }
  }, [updateNodeLabel]);

  // Import content from library into a node
  const importContentLibraryItem = useCallback((nodeId: string, contentId: string) => {
    const item = contentLibraryItems.find((c) => c.id === contentId);
    if (!item) return;

    const contentForms: ContentForm[] = LOCALES.map((l) => {
      const loc = item.locales.find((il) => il.locale === l.code);
      return {
        locale: l.code,
        title: loc?.title ?? '',
        body: loc?.body ?? '',
        imageUrl: (loc?.mediaUrls ?? [])[0] ?? '',
        videoUrl: item.contentType === 'video' ? ((loc?.mediaUrls ?? [])[0] ?? '') : '',
        pushTitle: loc?.pushTitle ?? '',
        pushBody: loc?.pushBody ?? '',
      };
    });

    setNodeContentMap((prev) => {
      const next = new Map(prev);
      next.set(nodeId, contentForms);
      return next;
    });

    // Update label from ES title
    const esLocale = item.locales.find((l) => l.locale === 'es');
    if (esLocale?.title) {
      updateNodeLabel(nodeId, esLocale.title);
    }

    // Store reference in node config
    updateNodeConfig(nodeId, { contentLibraryId: contentId });
  }, [contentLibraryItems, updateNodeLabel, updateNodeConfig]);

  // ─── Validation ──────────────────────────────────────

  function validateGraph(): string | null {
    const triggerNodes = nodes.filter((n) => n.type === 'trigger');
    if (triggerNodes.length === 0) return 'Journey must have at least one trigger node.';
    if (triggerNodes.length > 1) return 'Journey should have exactly one trigger node.';

    // Check all nodes are reachable from trigger
    const reachable = new Set<string>();
    const queue = [triggerNodes[0].id];
    while (queue.length > 0) {
      const nodeId = queue.shift()!;
      if (reachable.has(nodeId)) continue;
      reachable.add(nodeId);
      for (const edge of edges) {
        if (edge.source === nodeId && !reachable.has(edge.target)) {
          queue.push(edge.target);
        }
      }
    }

    const unreachable = nodes.filter((n) => !reachable.has(n.id));
    if (unreachable.length > 0) return `${unreachable.length} node(s) are not reachable from the trigger.`;

    // Check condition nodes have both yes/no edges
    for (const node of nodes) {
      if (node.type === 'condition') {
        const nodeEdges = edges.filter((e) => e.source === node.id);
        const hasYes = nodeEdges.some((e) => e.sourceHandle === 'yes');
        const hasNo = nodeEdges.some((e) => e.sourceHandle === 'no');
        if (!hasYes || !hasNo) {
          return 'Condition nodes must have both "Sí" and "No" connections.';
        }
      }
    }

    return null;
  }

  // ─── Save ────────────────────────────────────────────

  const handleSave = async () => {
    const error = validateGraph();
    if (error) {
      setValidationError(error);
      return;
    }
    setValidationError(null);
    setSaving(true);

    try {
      const payload = {
        name,
        description: description || undefined,
        priority: parseInt(priority) || 0,
        active,
        products: selectedProducts.map((p) => p.id),
        nodes: nodes.map((n) => ({
          tempId: n.id,
          nodeType: n.type!,
          config: (n.data.config ?? {}) as Record<string, unknown>,
          positionX: n.position.x,
          positionY: n.position.y,
          content: (nodeContentMap.get(n.id) ?? [])
            .filter((c: ContentForm) => c.title.trim() || c.body.trim() || c.pushTitle.trim() || c.pushBody.trim())
            .map((c: ContentForm) => ({
              locale: c.locale,
              title: c.title,
              body: c.body,
              imageUrl: c.imageUrl || undefined,
              videoUrl: c.videoUrl || undefined,
              pushTitle: c.pushTitle,
              pushBody: c.pushBody,
            })),
        })),
        edges: edges.map((e) => ({
          sourceTempId: e.source,
          targetTempId: e.target,
          sourceHandle: e.sourceHandle ?? 'default',
        })),
      };

      if (journeyId) {
        await apiPut(`/panel-api/journeys/${journeyId}`, payload);
      } else {
        await apiPost('/panel-api/journeys', payload);
      }

      onBack();
    } catch (err) {
      console.error('Failed to save journey:', err);
      setValidationError('Failed to save. Check console for details.');
    } finally {
      setSaving(false);
    }
  };

  // ─── Selected Node ───────────────────────────────────

  const selectedNode = nodes.find((n) => n.id === selectedNodeId);
  const selectedConfig = ((selectedNode?.data?.config ?? {}) as Record<string, unknown>);
  const selectedContent = selectedNodeId ? nodeContentMap.get(selectedNodeId) : undefined;

  // Auto-initialize content map for survey nodes (for push title/body)
  useEffect(() => {
    if (selectedNodeId && selectedNode?.type === 'survey' && !nodeContentMap.has(selectedNodeId)) {
      const emptyContent: ContentForm[] = LOCALES.map((l) => ({
        locale: l.code, title: '', body: '', imageUrl: '', videoUrl: '', pushTitle: '', pushBody: '',
      }));
      setNodeContentMap((prev) => new Map(prev).set(selectedNodeId, emptyContent));
    }
  }, [selectedNodeId, selectedNode?.type]);

  // ─── Render ──────────────────────────────────────────

  if (loading) {
    return (
      <Page title="Loading...">
        <div style={{ textAlign: 'center', padding: 48 }}><Spinner size="large" /></div>
      </Page>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {/* Top bar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '8px 16px',
        borderBottom: '1px solid #e5e7eb',
        background: '#fff',
        zIndex: 10,
      }}>
        <Button icon={ArrowLeftIcon} onClick={onBack} variant="plain" />
        <TextField
          label=""
          labelHidden
          value={name}
          onChange={setName}
          placeholder="Journey name..."
          autoComplete="off"
          connectedRight={
            <Select
              label=""
              labelHidden
              options={[{ label: 'Active', value: 'true' }, { label: 'Inactive', value: 'false' }]}
              value={String(active)}
              onChange={(v) => setActive(v === 'true')}
            />
          }
        />
        <TextField label="" labelHidden type="number" value={priority} onChange={setPriority} autoComplete="off" placeholder="Priority" />
        <div style={{ flex: 1 }} />
        {validationError && (
          <Text as="span" tone="critical">{validationError}</Text>
        )}
        <Button variant="primary" onClick={handleSave} disabled={saving || !name.trim()}>
          {saving ? 'Saving...' : 'Guardar'}
        </Button>
        {analytics && journeyId && (
          <span style={{ fontSize: 11, color: '#6b7280' }}>
            📊 {analytics.totalEnrollments} enrollados · {analytics.completedEnrollments} completados
          </span>
        )}
        {journeyId && analytics && analytics.completedEnrollments > 0 && (
          <Button
            size="slim"
            variant="plain"
            disabled={reenrolling}
            onClick={async () => {
              if (!selectedNodeId) {
                alert('Selecciona un nodo destino en el canvas primero');
                return;
              }
              if (!confirm(`¿Re-enrollar ${analytics.completedEnrollments} usuarios completados al nodo seleccionado?`)) return;
              setReenrolling(true);
              try {
                const res = await apiPost(`/panel-api/journeys/${journeyId}/reenroll`, { targetNodeId: selectedNodeId });
                alert(res.message);
                // Refresh analytics
                const data = await apiGet<any>(`/panel-api/journeys/${journeyId}/analytics`);
                setAnalytics(data);
              } catch (err) {
                alert('Error re-enrollando');
              } finally {
                setReenrolling(false);
              }
            }}
          >
            {reenrolling ? 'Re-enrollando...' : `🔄 Re-enrollar completados (${analytics.completedEnrollments})`}
          </Button>
        )}
      </div>

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Left sidebar — Node palette */}
        <div style={{
          width: 200,
          borderRight: '1px solid #e5e7eb',
          padding: 12,
          overflowY: 'auto',
          background: '#fafafa',
        }}>
          <Text as="h3" variant="headingSm">Nodes</Text>
          <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {NODE_TYPE_OPTIONS
              .filter((opt) => opt.value !== 'trigger' || !nodes.some((n) => n.type === 'trigger'))
              .map((opt) => (
              <div
                key={opt.value}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData('application/reactflow', opt.value);
                  e.dataTransfer.effectAllowed = 'move';
                }}
                style={{
                  padding: '8px 10px',
                  borderRadius: 8,
                  border: `2px solid ${opt.color}`,
                  background: '#fff',
                  cursor: 'grab',
                  fontSize: 13,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <span>{opt.icon}</span>
                <span>{opt.label.replace(/^. /, '')}</span>
              </div>
            ))}
          </div>

        </div>

        {/* Center — React Flow canvas */}
        <div ref={reactFlowWrapper} style={{ flex: 1, position: 'relative' }}>
          <ReactFlow
            nodes={nodes}
            edges={styledEdges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnectWrapped}
            onConnectStart={onConnectStart}
            onConnectEnd={onConnectEnd}
            onInit={setRfInstance}
            onNodeClick={onNodeClick}
            onEdgeClick={onEdgeClick}
            onPaneClick={onPaneClick}
            onDragOver={onDragOver}
            onDrop={onDrop}
            nodeTypes={nodeTypes}
            defaultEdgeOptions={{
              markerEnd: { type: MarkerType.ArrowClosed },
              style: { strokeWidth: 2 },
            }}
            fitView
            deleteKeyCode="Delete"
          >
            <Background />
            <Controls />
            <MiniMap
              nodeStrokeWidth={3}
              nodeColor={(n) => {
                const s = nodeStyles[n.type as keyof typeof nodeStyles];
                return s?.borderColor ?? '#999';
              }}
            />
          </ReactFlow>

          {/* Drop-to-create context menu */}
          {dropMenu && (
            <>
              <div
                onClick={closeDropMenu}
                style={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 50,
                }}
              />
              <div
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                style={{
                  position: 'absolute',
                  left: dropMenu.x,
                  top: dropMenu.y,
                  zIndex: 51,
                  background: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: 10,
                  boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                  padding: 6,
                  minWidth: 170,
                }}
              >
                <div style={{ padding: '4px 8px', fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Crear nodo
                </div>
                {NODE_TYPE_OPTIONS.filter((o) => o.value !== 'trigger').map((opt) => (
                  <div
                    key={opt.value}
                    onClick={() => createNodeFromDrop(opt.value)}
                    style={{
                      padding: '7px 10px',
                      cursor: 'pointer',
                      fontSize: 13,
                      fontWeight: 500,
                      borderRadius: 6,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = '#f3f4f6')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <span style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: opt.color,
                      flexShrink: 0,
                    }} />
                    <span>{opt.icon} {opt.label.replace(/^. /, '')}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Right sidebar — Edge properties */}
        {selectedEdgeId && !selectedNode && (
          <div style={{
            width: 320,
            borderLeft: '1px solid #e5e7eb',
            padding: 16,
            overflowY: 'auto',
            background: '#fff',
          }}>
            <BlockStack gap="400">
              <Text as="h3" variant="headingMd">Connection</Text>
              <Text as="p" tone="subdued" variant="bodySm">
                {(() => {
                  const edge = edges.find((e) => e.id === selectedEdgeId);
                  if (!edge) return '';
                  const sourceNode = nodes.find((n) => n.id === edge.source);
                  const targetNode = nodes.find((n) => n.id === edge.target);
                  const sourceLabel = sourceNode ? (NODE_TYPE_OPTIONS.find((o) => o.value === sourceNode.type)?.label ?? sourceNode.type) : edge.source;
                  const targetLabel = targetNode ? (NODE_TYPE_OPTIONS.find((o) => o.value === targetNode.type)?.label ?? targetNode.type) : edge.target;
                  const handleLabel = edge.sourceHandle && edge.sourceHandle !== 'default' ? ` (${edge.sourceHandle})` : '';
                  return `${sourceLabel}${handleLabel} → ${targetLabel}`;
                })()}
              </Text>
              <Button icon={DeleteIcon} tone="critical" onClick={() => deleteEdge(selectedEdgeId)}>
                Eliminar conexión
              </Button>
              <Text as="p" tone="subdued" variant="bodySm">
                También puedes pulsar Delete o Backspace para eliminarla.
              </Text>
            </BlockStack>
          </div>
        )}

        {/* Right sidebar — Properties panel */}
        {selectedNode && (
          <div style={{
            width: 320,
            borderLeft: '1px solid #e5e7eb',
            padding: 16,
            overflowY: 'auto',
            background: '#fff',
          }}>
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <Text as="h3" variant="headingMd">
                  {NODE_TYPE_OPTIONS.find((o) => o.value === selectedNode.type)?.label ?? 'Node'}
                </Text>
                <Button icon={DeleteIcon} tone="critical" size="slim" onClick={() => deleteNode(selectedNode.id)} />
              </InlineStack>

              {/* Trigger config */}
              {selectedNode.type === 'trigger' && (
                <>
                  <Select
                    label="Trigger type"
                    options={TRIGGER_TYPES.filter((t) => !t.disabled)}
                    value={(selectedConfig.triggerType as string) ?? 'order_placed'}
                    onChange={(v) => updateNodeConfig(selectedNodeId!, { triggerType: v })}
                  />

                  {/* Products — only for product-related triggers */}
                  {PRODUCT_TRIGGER_TYPES.has((selectedConfig.triggerType as string) ?? 'order_placed') && (
                    <>
                      <Divider />
                      <Text as="h4" variant="headingSm">Productos</Text>
                      <Text as="p" variant="bodySm" tone="subdued">
                        {(selectedConfig.triggerType as string) === 'specific_product'
                          ? 'Producto que activa este trigger'
                          : 'Filtra por productos específicos (vacío = todos)'}
                      </Text>
                      {selectedProducts.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {selectedProducts.map((p) => (
                            <Tag key={p.id} onRemove={() => setSelectedProducts(selectedProducts.filter((pp) => pp.id !== p.id))}>
                              {p.title.slice(0, 25)}
                            </Tag>
                          ))}
                        </div>
                      )}
                      <TextField
                        label=""
                        labelHidden
                        value={productSearch}
                        onChange={setProductSearch}
                        autoComplete="off"
                        placeholder="Buscar productos..."
                        suffix={searchingProducts ? <Spinner size="small" /> : undefined}
                      />
                      {productResults.length > 0 && (
                        <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 4 }}>
                          {productResults.map((p) => (
                            <div
                              key={p.id}
                              onClick={() => {
                                if (!selectedProducts.find((pp) => pp.id === p.id)) {
                                  setSelectedProducts([...selectedProducts, p]);
                                }
                                setProductSearch('');
                                setProductResults([]);
                              }}
                              style={{ padding: '6px 8px', cursor: 'pointer', fontSize: 12, borderRadius: 4 }}
                              onMouseEnter={(e) => (e.currentTarget.style.background = '#e5e7eb')}
                              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                            >
                              {p.title}
                            </div>
                          ))}
                        </div>
                      )}
                      <Divider />
                    </>
                  )}

                  {/* nth_purchase: order number */}
                  {(selectedConfig.triggerType as string) === 'nth_purchase' && (
                    <TextField
                      label="Nº de pedido"
                      type="number"
                      value={String(selectedConfig.orderNumber ?? 2)}
                      onChange={(v) => updateNodeConfig(selectedNodeId!, { orderNumber: parseInt(v) || 2 })}
                      autoComplete="off"
                      helpText="Se activa cuando el cliente hace su pedido Nº X"
                    />
                  )}

                  {/* customer_inactive: days */}
                  {(selectedConfig.triggerType as string) === 'customer_inactive' && (
                    <TextField
                      label="Días de inactividad"
                      type="number"
                      value={String(selectedConfig.inactiveDays ?? 30)}
                      onChange={(v) => updateNodeConfig(selectedNodeId!, { inactiveDays: parseInt(v) || 30 })}
                      autoComplete="off"
                      helpText="Se activa si el cliente no compra en X días"
                    />
                  )}

                  {/* tier_change: direction */}
                  {(selectedConfig.triggerType as string) === 'tier_change' && (
                    <Select
                      label="Dirección del cambio"
                      options={TIER_DIRECTION_OPTIONS}
                      value={(selectedConfig.direction as string) ?? 'any'}
                      onChange={(v) => updateNodeConfig(selectedNodeId!, { direction: v })}
                    />
                  )}

                  {/* streak_milestone: days */}
                  {(selectedConfig.triggerType as string) === 'streak_milestone' && (
                    <Select
                      label="Hito de streak (días)"
                      options={STREAK_MILESTONE_OPTIONS}
                      value={String(selectedConfig.days ?? 7)}
                      onChange={(v) => updateNodeConfig(selectedNodeId!, { days: parseInt(v) || 7 })}
                    />
                  )}

                  {/* points_milestone: points threshold */}
                  {(selectedConfig.triggerType as string) === 'points_milestone' && (
                    <TextField
                      label="Umbral de puntos"
                      type="number"
                      value={String(selectedConfig.points ?? 500)}
                      onChange={(v) => updateNodeConfig(selectedNodeId!, { points: parseInt(v) || 500 })}
                      autoComplete="off"
                      helpText="Se activa cuando el balance alcanza X puntos"
                    />
                  )}

                  {/* specific_date: date picker + recurring */}
                  {(selectedConfig.triggerType as string) === 'specific_date' && (
                    <>
                      <TextField
                        label="Fecha (YYYY-MM-DD)"
                        value={(selectedConfig.date as string) ?? ''}
                        onChange={(v) => updateNodeConfig(selectedNodeId!, { date: v })}
                        autoComplete="off"
                        placeholder="2026-03-01"
                        helpText="La fecha en la que se activa el trigger"
                      />
                      <Select
                        label="¿Recurrente?"
                        options={[
                          { label: 'No — solo una vez', value: 'false' },
                          { label: 'Sí — cada año', value: 'true' },
                        ]}
                        value={String(selectedConfig.recurring ?? false)}
                        onChange={(v) => updateNodeConfig(selectedNodeId!, { recurring: v === 'true' })}
                      />
                    </>
                  )}
                </>
              )}

              {/* Timer config */}
              {selectedNode.type === 'timer' && (
                <>
                  <Select
                    label="Delay type"
                    options={DELAY_TYPES}
                    value={(selectedConfig.delayType as string) ?? 'relative'}
                    onChange={(v) => updateNodeConfig(selectedNodeId!, { delayType: v })}
                  />
                  {(selectedConfig.delayType ?? 'relative') === 'relative' ? (
                    <TextField
                      label="Delay (hours)"
                      type="number"
                      value={String(selectedConfig.delayHours ?? 24)}
                      onChange={(v) => updateNodeConfig(selectedNodeId!, { delayHours: parseInt(v) || 0 })}
                      autoComplete="off"
                    />
                  ) : (
                    <TextField
                      label="Date/Time (ISO)"
                      value={(selectedConfig.delayDate as string) ?? ''}
                      onChange={(v) => updateNodeConfig(selectedNodeId!, { delayDate: v })}
                      autoComplete="off"
                      placeholder="2026-03-01T10:00:00Z"
                    />
                  )}
                </>
              )}

              {/* Condition config */}
              {selectedNode.type === 'condition' && (
                <>
                  <Select
                    label="Condition type"
                    options={CONDITION_TYPES}
                    value={(selectedConfig.conditionType as string) ?? 'survey_response'}
                    onChange={(v) => updateNodeConfig(selectedNodeId!, { conditionType: v })}
                  />

                  {/* No purchase in X days — custom UI */}
                  {(selectedConfig.conditionType as string) === 'no_purchase_in_days' && (
                    <TextField
                      label="Días sin compra"
                      type="number"
                      value={String(selectedConfig.days ?? 30)}
                      onChange={(v) => updateNodeConfig(selectedNodeId!, { days: parseInt(v) || 30 })}
                      autoComplete="off"
                      helpText="Sí = no ha comprado en estos días. No = ha comprado."
                    />
                  )}

                  {/* Has purchased product — product selector */}
                  {(selectedConfig.conditionType as string) === 'has_purchased_product' && (
                    <>
                      {selectedConfig.productTitle && (
                        <Tag onRemove={() => updateNodeConfig(selectedNodeId!, { shopifyProductId: '', productTitle: '' })}>
                          {(selectedConfig.productTitle as string).slice(0, 30)}
                        </Tag>
                      )}
                      {!selectedConfig.shopifyProductId && (
                        <>
                          <TextField
                            label="Buscar producto"
                            value={productSearch}
                            onChange={setProductSearch}
                            autoComplete="off"
                            placeholder="Buscar productos..."
                            suffix={searchingProducts ? <Spinner size="small" /> : undefined}
                          />
                          {productResults.length > 0 && (
                            <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 4 }}>
                              {productResults.map((p) => (
                                <div
                                  key={p.id}
                                  onClick={() => {
                                    updateNodeConfig(selectedNodeId!, { shopifyProductId: p.id, productTitle: p.title });
                                    setProductSearch('');
                                    setProductResults([]);
                                  }}
                                  style={{ padding: '6px 8px', cursor: 'pointer', fontSize: 12, borderRadius: 4 }}
                                  onMouseEnter={(e) => (e.currentTarget.style.background = '#e5e7eb')}
                                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                                >
                                  {p.title}
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                      <Text as="p" variant="bodySm" tone="subdued">
                        Sí = el cliente ha comprado este producto. No = no lo ha comprado.
                      </Text>
                    </>
                  )}

                  {/* Shipping status — select dropdown */}
                  {(selectedConfig.conditionType as string) === 'shipping_status' && (
                    <>
                      <Select
                        label="Operador"
                        options={[
                          { label: 'Igual a', value: 'equals' },
                          { label: 'Distinto de', value: 'not_equals' },
                        ]}
                        value={(selectedConfig.operator as string) ?? 'equals'}
                        onChange={(v) => updateNodeConfig(selectedNodeId!, { operator: v })}
                      />
                      <Select
                        label="Estado de envío"
                        options={[
                          { label: 'Confirmado', value: 'confirmed' },
                          { label: 'En preparación', value: 'preparing' },
                          { label: 'Enviado', value: 'shipped' },
                          { label: 'En tránsito', value: 'in_transit' },
                          { label: 'En reparto', value: 'out_for_delivery' },
                          { label: 'Entregado', value: 'delivered' },
                          { label: 'Incidencia', value: 'exception' },
                          { label: 'Devuelto', value: 'returned' },
                        ]}
                        value={(selectedConfig.value as string) ?? 'delivered'}
                        onChange={(v) => updateNodeConfig(selectedNodeId!, { value: v })}
                      />
                      <Text as="p" variant="bodySm" tone="subdued">
                        Sí = el envío está en este estado. No = el envío NO está en este estado.
                      </Text>
                    </>
                  )}

                  {/* Generic operator + value for other conditions */}
                  {!['no_purchase_in_days', 'has_purchased_product', 'shipping_status'].includes(selectedConfig.conditionType as string) && (
                    <>
                      <Select
                        label="Operator"
                        options={OPERATORS}
                        value={(selectedConfig.operator as string) ?? 'equals'}
                        onChange={(v) => updateNodeConfig(selectedNodeId!, { operator: v })}
                      />
                      <TextField
                        label="Value"
                        value={(selectedConfig.value as string) ?? ''}
                        onChange={(v) => updateNodeConfig(selectedNodeId!, { value: v })}
                        autoComplete="off"
                      />
                    </>
                  )}

                  {((selectedConfig.conditionType as string) === 'survey_response' ||
                    (selectedConfig.conditionType as string) === 'survey_completed') && (
                    <>
                      <Select
                        label="Survey"
                        options={[
                          { label: '— Select —', value: '' },
                          ...surveyOptions.map((s) => ({ label: s.title, value: s.id })),
                        ]}
                        value={(selectedConfig.surveyId as string) ?? ''}
                        onChange={(v) => updateNodeConfig(selectedNodeId!, { surveyId: v })}
                      />
                      {(selectedConfig.conditionType as string) === 'survey_response' && (
                        <TextField
                          label="Question index"
                          type="number"
                          value={String(selectedConfig.questionIndex ?? 0)}
                          onChange={(v) => updateNodeConfig(selectedNodeId!, { questionIndex: parseInt(v) || 0 })}
                          autoComplete="off"
                        />
                      )}
                    </>
                  )}
                </>
              )}

              {/* Survey config */}
              {selectedNode.type === 'survey' && (
                <>
                  <Select
                    label="Survey"
                    options={[
                      { label: '— Select survey —', value: '' },
                      ...surveyOptions.map((s) => ({ label: s.title, value: s.id })),
                    ]}
                    value={(selectedConfig.surveyId as string) ?? ''}
                    onChange={(v) => {
                      updateNodeConfig(selectedNodeId!, { surveyId: v });
                      const survey = surveyOptions.find((s) => s.id === v);
                      updateNodeLabel(selectedNodeId!, survey ? `Survey: ${survey.title}` : 'Select survey');
                    }}
                  />
                  <Select
                    label="Send push"
                    options={[{ label: 'Yes', value: 'true' }, { label: 'No', value: 'false' }]}
                    value={String(selectedConfig.sendPush ?? true)}
                    onChange={(v) => updateNodeConfig(selectedNodeId!, { sendPush: v === 'true' })}
                  />
                  {selectedConfig.sendPush !== false && (
                    <>
                      <Divider />
                      <Text as="h4" variant="headingSm">Push notification per language</Text>
                      <Tabs
                        tabs={LOCALES.map((l) => ({
                          id: `survey-push-${selectedNodeId}-${l.code}`,
                          content: l.name,
                        }))}
                        selected={localeTab}
                        onSelect={setLocaleTab}
                      >
                        <div style={{ paddingTop: 12 }}>
                          <BlockStack gap="300">
                            <TextField
                              label="Push title"
                              value={selectedContent?.[localeTab]?.pushTitle ?? ''}
                              onChange={(v) => updateNodeContent(selectedNodeId!, localeTab, 'pushTitle', v)}
                              autoComplete="off"
                            />
                            <TextField
                              label="Push body"
                              value={selectedContent?.[localeTab]?.pushBody ?? ''}
                              onChange={(v) => updateNodeContent(selectedNodeId!, localeTab, 'pushBody', v)}
                              autoComplete="off"
                            />
                          </BlockStack>
                        </div>
                      </Tabs>
                    </>
                  )}
                </>
              )}

              {/* Action config */}
              {selectedNode.type === 'action' && (
                <>
                  <Select
                    label="Tipo de acción"
                    options={ACTION_TYPES}
                    value={(selectedConfig.actionType as string) ?? 'award_points'}
                    onChange={(v) => updateNodeConfig(selectedNodeId!, { actionType: v })}
                  />

                  {/* Award points */}
                  {(selectedConfig.actionType as string) === 'award_points' && (
                    <TextField
                      label="Puntos"
                      type="number"
                      value={String(selectedConfig.points ?? 10)}
                      onChange={(v) => updateNodeConfig(selectedNodeId!, { points: parseInt(v) || 0 })}
                      autoComplete="off"
                    />
                  )}

                  {/* Award badge */}
                  {(selectedConfig.actionType as string) === 'award_badge' && (
                    <Select
                      label="Badge"
                      options={[
                        { label: '— Seleccionar badge —', value: '' },
                        ...BADGE_OPTIONS.map((b) => ({ label: b.label, value: b.slug })),
                      ]}
                      value={(selectedConfig.badgeSlug as string) ?? ''}
                      onChange={(v) => updateNodeConfig(selectedNodeId!, { badgeSlug: v })}
                    />
                  )}

                  {/* Generate discount */}
                  {(selectedConfig.actionType as string) === 'generate_discount' && (
                    <>
                      <Select
                        label="Tipo de descuento"
                        options={DISCOUNT_TYPES}
                        value={(selectedConfig.discountType as string) ?? 'percentage'}
                        onChange={(v) => updateNodeConfig(selectedNodeId!, { discountType: v })}
                      />
                      <TextField
                        label={(selectedConfig.discountType as string) === 'fixed_amount' ? 'Importe (€)' : 'Porcentaje (%)'}
                        type="number"
                        value={String(selectedConfig.discountValue ?? 10)}
                        onChange={(v) => updateNodeConfig(selectedNodeId!, { discountValue: parseInt(v) || 0 })}
                        autoComplete="off"
                      />
                      <TextField
                        label="Validez (días)"
                        type="number"
                        value={String(selectedConfig.expiryDays ?? 30)}
                        onChange={(v) => updateNodeConfig(selectedNodeId!, { expiryDays: parseInt(v) || 30 })}
                        autoComplete="off"
                        helpText="Días desde la generación hasta que caduca"
                      />
                      <Select
                        label="Uso"
                        options={[
                          { label: 'Un solo uso', value: '1' },
                          { label: 'Ilimitado', value: '0' },
                        ]}
                        value={String(selectedConfig.usageLimit ?? 1)}
                        onChange={(v) => updateNodeConfig(selectedNodeId!, { usageLimit: parseInt(v) })}
                      />
                      <Select
                        label="Enviar por push"
                        options={[{ label: 'Sí', value: 'true' }, { label: 'No', value: 'false' }]}
                        value={String(selectedConfig.sendPush ?? true)}
                        onChange={(v) => updateNodeConfig(selectedNodeId!, { sendPush: v === 'true' })}
                      />
                    </>
                  )}

                  {/* Enroll in journey */}
                  {(selectedConfig.actionType as string) === 'enroll_journey' && (
                    <Select
                      label="Journey destino"
                      options={[
                        { label: '— Seleccionar journey —', value: '' },
                        ...journeyOptions.map((j) => ({ label: j.name, value: j.id })),
                      ]}
                      value={(selectedConfig.journeyId as string) ?? ''}
                      onChange={(v) => updateNodeConfig(selectedNodeId!, { journeyId: v })}
                    />
                  )}

                  {/* Tag customer */}
                  {(selectedConfig.actionType as string) === 'tag_customer' && (
                    <TextField
                      label="Tag"
                      value={(selectedConfig.tag as string) ?? ''}
                      onChange={(v) => updateNodeConfig(selectedNodeId!, { tag: v })}
                      autoComplete="off"
                      placeholder="ej: completó_onboarding"
                      helpText="Se añade como tag al cliente"
                    />
                  )}
                </>
              )}

              {/* Review Request config */}
              {selectedNode.type === 'review_request' && (
                <>
                  <Select
                    label="Productos a reseñar"
                    options={[
                      { label: 'Productos del pedido (auto)', value: 'true' },
                      { label: 'Producto específico', value: 'false' },
                    ]}
                    value={String(selectedConfig.useOrderProducts ?? true)}
                    onChange={(v) => {
                      const useOrder = v === 'true';
                      updateNodeConfig(selectedNodeId!, {
                        useOrderProducts: useOrder,
                        ...(useOrder ? { specificProductId: '', specificProductHandle: '', specificProductTitle: '', specificProductImage: '' } : {}),
                      });
                      updateNodeLabel(selectedNodeId!, useOrder ? 'Review: Productos del pedido' : 'Review: Seleccionar producto');
                    }}
                  />

                  {selectedConfig.useOrderProducts === false && (
                    <>
                      {selectedConfig.specificProductTitle && (
                        <Tag onRemove={() => {
                          updateNodeConfig(selectedNodeId!, { specificProductId: '', specificProductHandle: '', specificProductTitle: '', specificProductImage: '' });
                          updateNodeLabel(selectedNodeId!, 'Review: Seleccionar producto');
                        }}>
                          {(selectedConfig.specificProductTitle as string).slice(0, 30)}
                        </Tag>
                      )}
                      {!selectedConfig.specificProductId && (
                        <>
                          <TextField
                            label="Buscar producto"
                            value={productSearch}
                            onChange={setProductSearch}
                            autoComplete="off"
                            placeholder="Buscar productos..."
                            suffix={searchingProducts ? <Spinner size="small" /> : undefined}
                          />
                          {productResults.length > 0 && (
                            <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 4 }}>
                              {productResults.map((p) => (
                                <div
                                  key={p.id}
                                  onClick={() => {
                                    const numericId = p.id.replace(/^gid:\/\/shopify\/Product\//, '');
                                    updateNodeConfig(selectedNodeId!, {
                                      specificProductId: numericId,
                                      specificProductHandle: p.handle,
                                      specificProductTitle: p.title,
                                      specificProductImage: p.image ?? '',
                                    });
                                    updateNodeLabel(selectedNodeId!, `Review: ${p.title}`);
                                    setProductSearch('');
                                    setProductResults([]);
                                  }}
                                  style={{ padding: '6px 8px', cursor: 'pointer', fontSize: 12, borderRadius: 4 }}
                                  onMouseEnter={(e) => (e.currentTarget.style.background = '#e5e7eb')}
                                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                                >
                                  {p.title}
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </>
                  )}

                  <Divider />
                  <Select
                    label="Enviar push"
                    options={[{ label: 'Sí', value: 'true' }, { label: 'No', value: 'false' }]}
                    value={String(selectedConfig.sendPush ?? true)}
                    onChange={(v) => updateNodeConfig(selectedNodeId!, { sendPush: v === 'true' })}
                  />
                  {selectedConfig.sendPush !== false && (
                    <>
                      <Divider />
                      <Text as="h4" variant="headingSm">Push notification per language</Text>
                      <Tabs
                        tabs={LOCALES.map((l) => ({
                          id: `review-push-${selectedNodeId}-${l.code}`,
                          content: l.name,
                        }))}
                        selected={localeTab}
                        onSelect={setLocaleTab}
                      >
                        <div style={{ paddingTop: 12 }}>
                          <BlockStack gap="300">
                            <TextField
                              label="Push title"
                              value={selectedContent?.[localeTab]?.pushTitle ?? ''}
                              onChange={(v) => updateNodeContent(selectedNodeId!, localeTab, 'pushTitle', v)}
                              autoComplete="off"
                              placeholder="¡Cuéntanos qué tal tu compra!"
                            />
                            <TextField
                              label="Push body"
                              value={selectedContent?.[localeTab]?.pushBody ?? ''}
                              onChange={(v) => updateNodeContent(selectedNodeId!, localeTab, 'pushBody', v)}
                              autoComplete="off"
                              placeholder="Deja tu opinión y gana puntos"
                            />
                          </BlockStack>
                        </div>
                      </Tabs>
                    </>
                  )}
                  <Divider />
                  <Text as="p" variant="bodySm" tone="subdued">
                    Los puntos concedidos son los configurados en Ajustes &gt; Puntos &gt; Reseña de producto.
                  </Text>
                </>
              )}

              {/* Content config (for content nodes only) */}
              {selectedNode.type === 'content' && selectedContent && (
                <>
                  {/* Content Library selector */}
                  {contentLibraryItems.length > 0 && (
                    <>
                      <Select
                        label="Importar de la biblioteca"
                        options={[
                          { label: '— Escribir contenido inline —', value: '' },
                          ...contentLibraryItems.map((c) => ({
                            label: `${c.contentType === 'video' ? '🎥' : c.contentType === 'image_gallery' ? '🖼️' : '📝'} ${c.internalName}`,
                            value: c.id,
                          })),
                        ]}
                        value={(selectedConfig.contentLibraryId as string) ?? ''}
                        onChange={(v) => {
                          if (v) {
                            importContentLibraryItem(selectedNodeId!, v);
                          } else {
                            updateNodeConfig(selectedNodeId!, { contentLibraryId: undefined });
                          }
                        }}
                      />
                      <Divider />
                    </>
                  )}

                  <Select
                    label="Send push"
                    options={[{ label: 'Yes', value: 'true' }, { label: 'No', value: 'false' }]}
                    value={String(selectedConfig.sendPush ?? true)}
                    onChange={(v) => updateNodeConfig(selectedNodeId!, { sendPush: v === 'true' })}
                  />

                  <Divider />
                  <Text as="h4" variant="headingSm">Content per language</Text>
                  <Tabs
                    tabs={LOCALES.map((l) => ({
                      id: `content-${selectedNodeId}-${l.code}`,
                      content: l.name,
                    }))}
                    selected={localeTab}
                    onSelect={setLocaleTab}
                  >
                    <div style={{ paddingTop: 12 }}>
                      <BlockStack gap="300">
                        <TextField
                          label="Title"
                          value={selectedContent[localeTab]?.title ?? ''}
                          onChange={(v) => updateNodeContent(selectedNodeId!, localeTab, 'title', v)}
                          autoComplete="off"
                        />
                        <TextField
                          label="Body (markdown)"
                          value={selectedContent[localeTab]?.body ?? ''}
                          onChange={(v) => updateNodeContent(selectedNodeId!, localeTab, 'body', v)}
                          multiline={4}
                          autoComplete="off"
                        />
                        <TextField
                          label="Image URL"
                          value={selectedContent[localeTab]?.imageUrl ?? ''}
                          onChange={(v) => updateNodeContent(selectedNodeId!, localeTab, 'imageUrl', v)}
                          autoComplete="off"
                        />
                        <TextField
                          label="Video URL"
                          value={selectedContent[localeTab]?.videoUrl ?? ''}
                          onChange={(v) => updateNodeContent(selectedNodeId!, localeTab, 'videoUrl', v)}
                          autoComplete="off"
                        />
                        <Divider />
                        <Text as="p" variant="bodySm" tone="subdued">Push notification</Text>
                        <TextField
                          label="Push title"
                          value={selectedContent[localeTab]?.pushTitle ?? ''}
                          onChange={(v) => updateNodeContent(selectedNodeId!, localeTab, 'pushTitle', v)}
                          autoComplete="off"
                        />
                        <TextField
                          label="Push body"
                          value={selectedContent[localeTab]?.pushBody ?? ''}
                          onChange={(v) => updateNodeContent(selectedNodeId!, localeTab, 'pushBody', v)}
                          autoComplete="off"
                        />
                      </BlockStack>
                    </div>
                  </Tabs>
                </>
              )}
            </BlockStack>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Helpers ───────────────────────────────────────────────────

function getDefaultConfig(nodeType: string): Record<string, unknown> {
  switch (nodeType) {
    case 'trigger':
      return { triggerType: 'order_placed' };
    case 'content':
      return { sendPush: true };
    case 'survey':
      return { surveyId: '', sendPush: true };
    case 'timer':
      return { delayType: 'relative', delayHours: 24 };
    case 'condition':
      return { conditionType: 'survey_response', operator: 'equals', value: '' };
    case 'action':
      return { actionType: 'award_points', points: 10 };
    case 'review_request':
      return { sendPush: true, useOrderProducts: true };
    default:
      return {};
  }
}
